'use client';

import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home, ArrowLeft, Bug } from 'lucide-react';
import { useTranslations } from "@/i18n/useTranslations";

export function ErrorUI({
  error,
  showDebug = process.env.NODE_ENV === 'development',
}: {
  error?: unknown;
  showDebug?: boolean;
}) {
  const routeError = useRouteError();
  const displayError = error ?? routeError;
  const navigate = useNavigate();
  const t = useTranslations("errors");

  let title = t('page.defaultTitle');
  let description = t('page.defaultDescription');
  let errorMessage = '';
  let statusCode = 0;

  if (isRouteErrorResponse(routeError)) {
    statusCode = routeError.status;
    switch (routeError.status) {
      case 404:
        title = t('page.notFoundTitle');
        description = t('page.notFoundDescription');
        break;
      case 403:
        title = t('page.forbiddenTitle');
        description = t('page.forbiddenDescription');
        break;
      case 500:
        title = t('page.serverErrorTitle');
        description = t('page.serverErrorDescription');
        break;
      case 401:
        title = t('page.unauthorizedTitle');
        description = t('page.unauthorizedDescription');
        break;
      default:
        title = t('page.defaultTitle');
        description = routeError.statusText || t('page.defaultDescription');
    }
    errorMessage = routeError.data?.message || routeError.statusText || '';
  } else if (displayError instanceof Error) {
    errorMessage = displayError.message;
  } else if (typeof displayError === 'string') {
    errorMessage = displayError;
  }

  const handleReload = () => {
    window.location.reload();
  };

  const canGoBack = window.history.length > 1;

  const handleGoBack = () => {
    if (canGoBack) {
      window.history.back();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[100vh] p-4 bg-gradient-to-br from-blue-50 via-sky-50 to-cyan-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-xl glass-card border-error/20 shadow-glass-lg relative p-2">
        <CardHeader className="text-center pb-4 pt-8">
          <div className="mx-auto mb-8 h-24 w-24 rounded-3xl bg-gradient-to-br from-destructive to-orange-500 flex items-center justify-center shadow-elev shadow-red-500/30">
            <AlertTriangle className="h-12 w-12 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gradient">{title}</CardTitle>
          <CardDescription className="text-lg mt-3">{description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-8 px-8 pb-8">
          {statusCode > 0 && (
            <div className="flex justify-center">
              <span className="px-4 py-1.5 rounded-full bg-error-soft text-error-text text-base font-medium">
                {t('page.errorCode')} {statusCode}
              </span>
            </div>
          )}

          {showDebug && errorMessage && (
            <div className="rounded-xl bg-slate-900 dark:bg-slate-950 p-5 border border-border">
              <div className="flex items-center gap-2 mb-3 text-red-400">
                <Bug className="h-4 w-4" />
                <span className="text-sm font-semibold uppercase tracking-wide">{t('page.errorDetails')}</span>
              </div>
              <pre className="text-sm text-muted-foreground font-mono overflow-auto max-h-48 leading-relaxed">
                {errorMessage}
              </pre>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            {canGoBack && (
              <Button
                variant="outline"
                size="lg"
                onClick={handleGoBack}
                className="flex-1 glass-button border-border dark:border-border h-12 text-base"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                {t('page.goBack')}
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              onClick={handleReload}
              className="flex-1 glass-button border-border dark:border-border h-12 text-base"
            >
              <RefreshCw className="mr-2 h-5 w-5" />
              {t('page.refresh')}
            </Button>
            <Button variant="default" size="lg" onClick={() => navigate('/')} className="flex-1 shadow-card shadow-primary/25 h-12 text-base">
              <Home className="mr-2 h-5 w-5" />
              {t('page.home')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function RouteErrorPage() {
  return <ErrorUI />;
}
